import React, { useCallback } from "react"
import { Col, Row } from "react-bootstrap"
import styled from "styled-components"
import { State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { EMPTY_MEDIA, useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { replacePlaceholder, useLanguageState } from "../../globalStates/LanguageState"
import { addElement, getDefaultContentLanguage, removeElementById } from "../../globalStates/utils"
import { useLanguageSwitch } from "../../utils/Language"
import Dropzone from "../../components/Dropzone"
import MutimediaItem from "./MultimediaItem"
import LanguageSwitch from "../languageSwitch"
import { addValidatorsMultimedia } from "../../globalStates/ExhibitorValidation"
import { Media } from "../../globalStates/ExhibitorDataTypes"

const MultimediaSectionWrapper = styled.div``

const MultimediaHeaders = styled.div`
    display: flex;
    border-bottom: 1px solid #d9d9d9;
    padding: 10px 0;
    margin: 25px -27.5px;
    & > * {
        margin: 0 27.5px;
    }
`

const MultimediaHeader = styled.div<{ width: number }>`
    flex: 0 0 ${(props) => (props.width ? `${props.width}px` : "1")};
`

interface MultimediaContentsProps {
    mediaStates: State<Media[], Validation>
    index?: number
    multimediaLimit: number
}

const MultimediaContents: React.FC<MultimediaContentsProps> = ({ mediaStates, index, multimediaLimit }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(getDefaultContentLanguage())
    const remainingUploads = multimediaLimit <= mediaStates.length ? 0 : multimediaLimit - mediaStates.length
    const onDropHandler = useCallback(
        (
            file: File,
            onUploadProgress: (progress: number) => void,
            onSuccessCallback: () => void,
            onErrorCallback: (reason: string) => void
        ) => {
            exiState
                .upload("MULTIMEDIA", file, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    if (response) {
                        addElement(mediaStates, {
                            ...EMPTY_MEDIA,
                            fileId: response.fileId,
                            name: response.fileName,
                            url: response.fileUrl,
                            contentType: response.contentType,
                            size: response.size,
                            order: mediaStates.length + 1
                        })
                        addValidatorsMultimedia(mediaStates[mediaStates.length - 1])
                    }
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, mediaStates]
    )
    const onRemoveMediaHandler = useCallback(
        (mediaId: string) => {
            removeElementById<Media>(mediaStates, mediaId)
        },
        [mediaStates]
    )
    return (
        <MultimediaSectionWrapper>
            <Row>
                <Col>
                    <LanguageSwitch
                        id={`multimedia_${index !== undefined ? index + "_" : ""}language_switch`}
                        name={`multimedia_${index !== undefined ? index + "_" : ""}languages`}
                        value={currentLanguage}
                        onChange={onChangeLanguageHandler}
                        entityState={mediaStates}
                    />
                </Col>
            </Row>
            <Dropzone
                accept="image/jpeg, image/png, video/mp4, application/pdf"
                onDropFile={onDropHandler}
                placeholderTitle={replacePlaceholder(branding.configuration.multimediaPage.sctMultimediaImage.hntDropzoneTitle, {
                    count: String(remainingUploads)
                })}
                placeholderText={branding.configuration.multimediaPage.sctMultimediaImage.hntDropzoneProp}
                readonly={mediaStates.length >= multimediaLimit}
            />
            <MultimediaHeaders>
                <MultimediaHeader width={60}>
                    <label>{branding.configuration.multimediaPage.sctMultimediaImage.lblHeaderPosition}</label>
                </MultimediaHeader>
                <MultimediaHeader width={133}>
                    <label>{branding.configuration.multimediaPage.sctMultimediaImage.lblHeaderPreview}</label>
                </MultimediaHeader>
                <MultimediaHeader width={0}>
                    <label>{branding.configuration.multimediaPage.sctMultimediaImage.lblHeaderFile}</label>
                </MultimediaHeader>
                <MultimediaHeader width={55}>&nbsp;</MultimediaHeader>
            </MultimediaHeaders>

            {mediaStates.map((mediaState) => (
                <MutimediaItem
                    key={mediaState.id.get() || mediaState.fileId.get()}
                    mediaState={mediaState}
                    currentLanguage={currentLanguage}
                    onRemoveMedia={onRemoveMediaHandler}
                />
            ))}
        </MultimediaSectionWrapper>
    )
}
export default MultimediaContents
